import EditIcon from '@mui/icons-material/Edit';
import { Box, Card, CardContent, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import type { EntitySpec, FieldDef } from '../../models/Models';
import { formatDecimal } from '../../utils/format';
import { DocumentFieldDisplay, DocumentsSectionDisplay } from './DocumentField';
import { displayName, iconButtonSx, renderBoolean, renderDateTime, renderValue } from './helpers';
import { useFkOptions } from './hooks';
import type { AnyEntity } from './types';

const FkValue = ({ field, value }: { field: FieldDef; value: unknown }) => {
  const { data } = useFkOptions(field.fkPath);
  if (value === null || value === undefined) return <>—</>;
  const match = data?.find((d) => d.id === value);
  return <>{match ? displayName(match) : `#${value}`}</>;
};

/** Renders a picture with a click-to-enlarge lightbox. */
const PictureValue = ({ src, alt }: { src: string; alt: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        component="img"
        src={src}
        alt={alt}
        onClick={() => setOpen(true)}
        sx={{
          maxWidth: 300,
          maxHeight: 200,
          objectFit: 'contain',
          borderRadius: 1,
          border: '1px solid #EEF1F5',
          bgcolor: '#FAFBFD',
          p: 0.5,
          cursor: 'pointer',
          transition: 'box-shadow 0.15s',
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <Box
          component="img"
          src={src}
          alt={alt}
          onClick={() => setOpen(false)}
          sx={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            display: 'block',
            cursor: 'pointer',
          }}
        />
      </Dialog>
    </>
  );
};

const FieldValueCell = ({ field, item, spec }: { field: FieldDef; item: AnyEntity; spec: EntitySpec }) => {
  const raw = item[field.name];
  const isEmpty = raw === null || raw === undefined || raw === '';
  // Document fields are NOT entity-row columns — they are managed via the
  // ``/documents`` API. Render the inline upload/download/delete UI when the
  // entity has an id (i.e. has been saved at least once).
  if (field.type === 'document' || field.type === 'documents') {
    if (!spec.entityType || typeof item.id !== 'number') {
      return (
        <Typography variant="body2" sx={{ color: '#B0BAC5', fontSize: '0.85rem' }}>
          Save the {spec.singular} first to attach documents.
        </Typography>
      );
    }
    return field.type === 'document' ? (
      <DocumentFieldDisplay entityType={spec.entityType} entityId={item.id} field={field} />
    ) : (
      <DocumentsSectionDisplay entityType={spec.entityType} entityId={item.id} field={field} />
    );
  }
  if (field.type === 'picture') {
    return isEmpty || typeof raw !== 'string' ? (
      <Typography variant="body2" sx={{ color: '#B0BAC5', fontSize: '0.85rem' }}>—</Typography>
    ) : (
      <PictureValue src={raw} alt={field.label} />
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        fontWeight: isEmpty ? 400 : 500,
        color: isEmpty ? '#B0BAC5' : 'text.primary',
        fontSize: '0.85rem',
      }}
    >
      {field.type === 'fk' ? (
        <FkValue field={field} value={raw} />
      ) : field.type === 'boolean' ? (
        renderBoolean(raw)
      ) : field.type === 'datetime' ? (
        renderDateTime(raw)
      ) : field.type === 'logo' ? (
        isEmpty || typeof raw !== 'string' ? (
          '—'
        ) : (
          <Box
            component="img"
            src={raw}
            alt={field.label}
            sx={{
              maxWidth: 160,
              maxHeight: 96,
              objectFit: 'contain',
              borderRadius: 1,
              border: '1px solid #EEF1F5',
              bgcolor: '#FAFBFD',
              p: 0.5,
            }}
          />
        )
      ) : field.type === 'number' && raw != null && raw !== '' ? (
        formatDecimal(Number(raw), field.decimals ?? 2).toString()
      ) : (
        renderValue(raw)
      )}
    </Typography>
  );
};

const rowBorderSx = {
  borderBottom: '1px dashed',
  borderColor: '#EEF1F5',
  '&:last-of-type': { borderBottom: 'none' },
} as const;

const FieldRow = ({ field, item, spec }: { field: FieldDef; item: AnyEntity; spec: EntitySpec }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
      gap: { xs: 0.25, sm: 2 },
      py: 1,
      ...rowBorderSx,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
      {field.label}
    </Typography>
    <FieldValueCell field={field} item={item} spec={spec} />
  </Box>
);

const formatRangeValue = (from: unknown, to: unknown): string => {
  const fmt = (v: unknown) => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isFinite(n) ? formatDecimal(n).toString() : String(v);
  };
  const f = fmt(from);
  const t = fmt(to);
  if (!f && !t) return '-';
  return `${f || '…'} - ${t || '…'}`;
};

/** Renders a ``range`` field as one row: label, "from - to", optional comment. */
const RangeRow = ({ field, item, spec }: { field: FieldDef; item: AnyEntity; spec: EntitySpec }) => {
  const r = field.range!;
  const value = formatRangeValue(item[r.from], item[r.to]);
  const isEmpty = value === '—';
  const hasComment = !!r.comment;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: hasComment ? '220px 1fr 1fr' : '220px 1fr' },
        gap: { xs: 0.25, sm: 2 },
        py: 1,
        ...rowBorderSx,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
        {field.label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: isEmpty ? 400 : 500,
          color: isEmpty ? '#B0BAC5' : 'text.primary',
          fontSize: '0.85rem',
        }}
      >
        {value}
      </Typography>
      {hasComment && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem', flexShrink: 0 }}>
            Comment
          </Typography>
          <FieldValueCell
            field={{ name: r.comment!, label: 'Comment', type: 'multiline' }}
            item={item}
            spec={spec}
          />
        </Box>
      )}
    </Box>
  );
};

/** Renders two adjacent fields side-by-side on a single row. Used both for
 *  the legacy "X" + "X comment" pairs and for any field that opted in via
 *  ``inlineWithNext``. */
const FieldPairRow = ({
  main,
  secondary,
  item,
  spec,
  secondaryLabel,
}: {
  main: FieldDef;
  secondary: FieldDef;
  item: AnyEntity;
  spec: EntitySpec;
  secondaryLabel?: string;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '220px 1fr 1fr' },
      gap: { xs: 0.25, sm: 2 },
      py: 1,
      ...rowBorderSx,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem' }}>
      {main.label}
    </Typography>
    <FieldValueCell field={main} item={item} spec={spec} />
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.8rem', flexShrink: 0 }}>
        {secondaryLabel ?? secondary.label}
      </Typography>
      <FieldValueCell field={secondary} item={item} spec={spec} />
    </Box>
  </Box>
);

type FieldEntry = FieldDef | { main: FieldDef; secondary: FieldDef; secondaryLabel?: string };

/** Pair consecutive fields when:
 *   - the field opted in via ``inlineWithNext``, or
 *   - the second label is the first label + " comment" (case-insensitive).
 */
const pairFields = (fields: FieldDef[]): FieldEntry[] => {
  const result: FieldEntry[] = [];
  let i = 0;
  while (i < fields.length) {
    const cur = fields[i];
    const next = fields[i + 1];
    if (next && cur.inlineWithNext) {
      result.push({ main: cur, secondary: next });
      i += 2;
    } else if (next && next.label.toLowerCase() === `${cur.label.toLowerCase()} comment`) {
      result.push({ main: cur, secondary: next, secondaryLabel: 'Comment' });
      i += 2;
    } else {
      result.push(cur);
      i += 1;
    }
  }
  return result;
};

/** Group fields by their `group` key, preserving insertion order. */
const groupFields = (fields: FieldDef[]): { group: string; fields: FieldDef[] }[] => {
  const order: string[] = [];
  const map = new Map<string, FieldDef[]>();
  for (const f of fields) {
    const g = f.group ?? 'Details';
    if (!map.has(g)) {
      order.push(g);
      map.set(g, []);
    }
    map.get(g)!.push(f);
  }
  return order.map((g) => ({ group: g, fields: map.get(g)! }));
};

const ProfileTab = ({ spec, item, onEdit }: { spec: EntitySpec; item: AnyEntity; onEdit?: (group: string) => void }) => {
  // Logo fields are rendered next to the name in the Detail hero, so we
  // skip them here to avoid showing the same image twice.
  const groups = useMemo(
    () => groupFields(spec.fields.filter((f) => f.type !== 'logo')),
    [spec],
  );

  return (
    <Stack spacing={2}>
      {groups.map(({ group, fields }) => (
        <Card key={group} variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: 'linear-gradient(180deg, #F8FAFD 0%, #FFFFFF 100%)',
            }}
          >
            <Box sx={{ width: 4, height: 16, borderRadius: 2, bgcolor: '#009DE0' }} />
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                color: '#002A5C',
                letterSpacing: '0.08em',
                fontSize: '0.72rem',
                lineHeight: 1,
                flex: 1,
              }}
            >
              {group}
            </Typography>
            {onEdit && (
              <Tooltip title={`Edit ${group}`}>
                <IconButton size="small" onClick={() => onEdit(group)} sx={iconButtonSx('edit')}>
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1.5 } }}>
            {pairFields(fields).map((entry) =>
              'main' in entry ? (
                <FieldPairRow
                  key={entry.main.name}
                  main={entry.main}
                  secondary={entry.secondary}
                  secondaryLabel={entry.secondaryLabel}
                  item={item}
                  spec={spec}
                />
              ) : entry.type === 'range' ? (
                <RangeRow key={entry.name} field={entry} item={item} spec={spec} />
              ) : (
                <FieldRow key={entry.name} field={entry} item={item} spec={spec} />
              ),
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default ProfileTab;
